/**
 * The course wizard's unsaved entry, held outside React.
 *
 * Entering a scorecard means LEAVING THE APP: the pars and stroke indexes are
 * on the club's own website, so a phone user swaps back and forth a dozen times
 * filling in eighteen holes. Coming back is an unmount at best (the tab bar
 * took them elsewhere) and a cold start at worst (iOS evicted the WebView) —
 * either way React state is gone, and with it every number they had typed.
 *
 * So the draft is written to localStorage on every change and read back on
 * mount. Keyed by SOCIETY as well as course because every other snapshot in
 * this app is (a shared key once showed one society's data under another), and
 * stamped with a time because a month-old half-typed course should not walk
 * back into a fresh wizard.
 */

const PREFIX = 'course_draft_';

/**
 * Draft shape version. Bumped when the wizard gains fields, so an older draft
 * can be MIGRATED rather than discarded — the whole point of the draft is that
 * it survives, and throwing away eighteen hand-typed holes because the app
 * learned about yardage would be the one failure it exists to prevent.
 *
 * v1 -> v2: tees gained `hole_lengths` / `length_unit`; the course gained
 *           `address`, `postcode` and `website`.
 */
export const DRAFT_VERSION = 2;

/**
 * Bring a stored draft up to the current shape. Missing fields come back
 * EMPTY, never undefined, because the wizard's inputs are controlled.
 */
export function migrateCourseDraft(draft, version) {
  if (!draft || typeof draft !== 'object') return draft;
  if (version >= DRAFT_VERSION) return draft;

  const migrated = {
    address: '',
    postcode: '',
    website: '',
    ...draft,
  };
  if (Array.isArray(draft.tees)) {
    migrated.tees = draft.tees.map(t => ({
      length_unit: '',
      ...t,
      // An older draft has no lengths at all; a corrupt one may have a short
      // array. Either way the wizard needs exactly 18 controlled cells.
      hole_lengths: Array.isArray(t.hole_lengths) && t.hole_lengths.length === 18
        ? t.hole_lengths
        : Array(18).fill(''),
    }));
  }
  return migrated;
}

// How long a draft is offered for at all...
export const DRAFT_TTL = 7 * 24 * 60 * 60 * 1000;
// ...and how long it still counts as "they were in the middle of this", which
// is what lets the Courses page reopen the wizard by itself after a reload.
export const DRAFT_RESUME_WINDOW = 6 * 60 * 60 * 1000;

/**
 * Storage key for one wizard. `courseId` is null for a new course, and `scope`
 * separates the namespaces the wizard serves — a society's own course 12 and
 * the global directory's course 12 are different courses.
 */
export const courseDraftKey = (societyId, courseId, scope = 'my') =>
  `${PREFIX}${societyId ?? 'none'}_${scope}_${courseId ?? 'new'}`;

/**
 * The stored entry, or null when there is none, it is unreadable, or it has
 * expired. Returns the envelope — callers that only want the content use
 * `.draft`; the Courses page also needs `.at` to decide about resuming.
 */
export function readCourseDraft(key) {
  if (!key) return null;
  try {
    const entry = JSON.parse(localStorage.getItem(key) || 'null');
    if (!entry || !entry.at || !entry.draft) return null;
    if (Date.now() - entry.at > DRAFT_TTL) {
      localStorage.removeItem(key);
      return null;
    }
    // A draft written before versioning existed is v1 by definition.
    return { ...entry, draft: migrateCourseDraft(entry.draft, entry.v || 1) };
  } catch {
    return null;
  }
}

export function writeCourseDraft(key, draft) {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ at: Date.now(), v: DRAFT_VERSION, draft }));
  } catch {
    /* quota, or private mode — typing must never throw */
  }
}

export function clearCourseDraft(key) {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

/** True while the draft is recent enough to reopen the wizard unasked. */
export const isDraftResumable = (entry) =>
  !!entry && Date.now() - entry.at <= DRAFT_RESUME_WINDOW;
